import { type Goal, type GoalTask } from '@fruitful/types';
import { GoalStatus, GoalTaskStatus } from '@fruitful/types';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomFamily, RESET } from 'jotai/utils';
import { Button as PRButton } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Column, type ColumnEditorOptions } from 'primereact/column';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
    type DataTableRowEditEvent,
    type DataTableRowReorderEvent,
} from 'primereact/datatable';
import { Dropdown, type DropdownChangeEvent } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import React, {
    type Dispatch,
    type SetStateAction,
    useCallback,
    useEffect,
    useState,
} from 'react';
import {
    BodyMediumRegular,
    BodySmallEmphasized,
    Icon,
    IconButton,
    TrashIcon,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../../../common/components/GroveButtons';
import { atomWithStorage } from '../../../../common/providers/StorageProvider';
import type { StorageKey } from '../../../../common/types/Storage';
import {
    adminUserAtom,
    createdGoalTaskAtom,
    mutatedGoalTaskAtom,
    updatedGoalTasksAtom,
} from '../../../entityAtoms';

// Includes only in-progress and completed tasks, with completed tasks sorted after in-progress tasks
const filterAndSortTasks = (tasks: GoalTask[]) => {
    const inProgressTasks = tasks
        .filter((task) => task.status === GoalTaskStatus.IN_PROGRESS)
        .sort((a, b) => a.position - b.position);
    const completedTasks = tasks
        .filter((task) => task.status === GoalTaskStatus.COMPLETE)
        .sort((a, b) => a.position - b.position);
    return [...inProgressTasks, ...completedTasks];
};

export const GoalTasksRenderer = ({
    goal,
    onGoalStatusChange,
}: {
    goal: Goal;
    onGoalStatusChange: (goalWithNewStatus: Goal) => void;
}) => {
    const adminUser = useAtomValue(adminUserAtom);
    const canEditGoals =
        adminUser.authorization.selectedMembership.goals.canEdit;
    const setMutatedGoalTask = useSetAtom(mutatedGoalTaskAtom);
    const setUpdatedGoalTasks = useSetAtom(updatedGoalTasksAtom);
    const [taskRows, setTaskRows] = useState<GoalTask[]>(
        filterAndSortTasks(goal.tasks),
    );
    const [editingRowIds, setEditingRowIds] = useState<string[]>([]);

    useEffect(() => {
        setTaskRows(filterAndSortTasks(goal.tasks));
    }, [goal]);

    const TaskStatusRenderer = (task: GoalTask) => {
        return (
            <Checkbox
                onChange={() => {
                    onRowCheckboxChange(task);
                }}
                checked={task.status === GoalTaskStatus.COMPLETE}
                style={{ opacity: 0.7 }}
                disabled={!canEditGoals}
            />
        );
    };
    const TaskDescriptionRenderer = (task: GoalTask) => {
        const isComplete = task.status === GoalTaskStatus.COMPLETE;
        return (
            <BodyMediumRegular
                color={isComplete ? '$gray800' : undefined}
                textDecorationLine={isComplete ? 'line-through' : undefined}
                whiteSpace="pre-wrap"
            >
                {task.description}
            </BodyMediumRegular>
        );
    };
    const DeleteRenderer = (task: GoalTask) => {
        return editingRowIds.includes(task.id) ? (
            <YStack justifyContent="center" alignItems="center">
                <IconButton
                    variant="tertiary"
                    tone="danger"
                    icon={TrashIcon}
                    onPress={() => {
                        onPressDelete(task.id);
                    }}
                />
            </YStack>
        ) : null;
    };

    const onRowEditToggle = ({ data }: DataTableRowEditEvent) => {
        const task = data as GoalTask;
        setEditingRowIds((prev) => {
            if (prev.includes(task.id)) {
                // onRowEditCancel
                return prev.filter((id) => id !== task.id);
            }
            // onRowEditInit
            return [...prev, task.id];
        });
    };

    const onRowEditComplete = ({
        index,
        newData,
    }: DataTableRowEditCompleteEvent) => {
        const updatedTask = newData as GoalTask;
        setMutatedGoalTask(updatedTask);
        setTaskRows((prev) => {
            const tasks = [...prev];
            tasks[index] = updatedTask;
            return tasks;
        });
        setEditingRowIds((prev) => {
            return prev.filter((id) => id !== newData.id);
        });
    };

    const onRowCheckboxChange = (task: GoalTask) => {
        const updatedTask = {
            ...task,
            status:
                task.status === GoalTaskStatus.COMPLETE
                    ? GoalTaskStatus.IN_PROGRESS
                    : GoalTaskStatus.COMPLETE,
        };
        setMutatedGoalTask(updatedTask);
        setTaskRows((prev) => {
            const tasks = [...prev].map((t) =>
                t.id === task.id ? updatedTask : t,
            );
            return filterAndSortTasks(tasks);
        });
    };

    const onPressDelete = (taskId: string) => {
        const taskToDelete = taskRows.find((task) => task.id === taskId);
        if (!taskToDelete) {
            return;
        }

        setMutatedGoalTask({ ...taskToDelete, status: GoalTaskStatus.DELETED });
        setTaskRows((prev) => {
            return [...prev].filter((task) => task.id !== taskId);
        });
        setEditingRowIds((prev) => {
            return prev.filter((id) => id !== taskToDelete.id);
        });
    };

    const onRowReorder = useCallback(
        ({ value: rowsInNewOrder }: DataTableRowReorderEvent<GoalTask[]>) => {
            const reorderedTasks = rowsInNewOrder.map((task, index) => {
                task.position = index;
                return task;
            });
            setUpdatedGoalTasks(reorderedTasks);
            setTaskRows(reorderedTasks);
        },
        [],
    );

    return (
        <YStack paddingHorizontal="3rem" paddingBottom="1.5rem">
            <DataTable
                dataKey="id"
                value={taskRows}
                size="small"
                showHeaders={false}
                reorderableRows
                onRowReorder={onRowReorder}
                editMode="row"
                onRowEditInit={onRowEditToggle}
                onRowEditCancel={onRowEditToggle}
                onRowEditComplete={onRowEditComplete}
                emptyMessage={<XStack height={0} />}
            >
                <Column field="position" header="" sortable hidden />
                <Column
                    field="status"
                    header=""
                    style={{ width: '1.5rem', padding: '4px' }}
                    body={TaskStatusRenderer}
                />
                <Column
                    field="description"
                    header=""
                    body={TaskDescriptionRenderer}
                    editor={(options) => TextAreaEditor(options)}
                />
                <Column
                    rowEditor
                    style={{ width: '3rem' }}
                    hidden={!canEditGoals}
                />
                <Column
                    field="status"
                    header=""
                    style={{ width: '3rem', paddingLeft: 0 }}
                    body={DeleteRenderer}
                    hidden={!canEditGoals || !editingRowIds.length}
                />
                <Column
                    rowReorder
                    style={{ width: '3rem' }}
                    hidden={!canEditGoals}
                />
            </DataTable>
            {canEditGoals && (
                <AddNewTask
                    goal={goal}
                    tasks={taskRows}
                    setTasks={setTaskRows}
                    onGoalStatusChange={onGoalStatusChange}
                />
            )}
        </YStack>
    );
};

// This creates one local storage entry per drafted goal task
// If we want to consolidate all into one entry, we can follow
// the pattern used for message drafts and expanded goals
const goalTaskDraftsAtomFamily = atomFamily((id: string) =>
    atomWithStorage(`goal_task_draft-${id}` as StorageKey, ''),
);

const AddNewTask = ({
    goal,
    tasks,
    setTasks,
    onGoalStatusChange,
}: {
    goal: Goal;
    tasks: GoalTask[];
    setTasks: Dispatch<SetStateAction<GoalTask[]>>;
    onGoalStatusChange: (goalWithNewStatus: Goal) => void;
}) => {
    const setCreatedGoalTask = useSetAtom(createdGoalTaskAtom);
    const [isAddingNewTask, setIsAddingNewTask] = useState(
        tasks.length === 0 &&
            [GoalStatus.DRAFT, GoalStatus.IN_PROGRESS].includes(goal.status),
    );
    const [newTaskDescription, setNewTaskDescription] = useAtom(
        goalTaskDraftsAtomFamily(goal.id),
    );

    const reset = () => {
        setNewTaskDescription(RESET);
        setIsAddingNewTask(false);
    };
    const onPressSubmit = () => {
        const newTask: GoalTask = {
            id: `new-${tasks.length}`,
            goalId: goal.id,
            description: newTaskDescription,
            status: GoalTaskStatus.IN_PROGRESS,
            position: tasks.length,
            createdAt: new Date().toISOString(),
        };
        setTasks((prev) => {
            return filterAndSortTasks([...prev, newTask]);
        });
        setCreatedGoalTask(newTask);
        setNewTaskDescription(RESET);
    };
    const onChangeGoalStatus = (
        e: DropdownChangeEvent | { value: GoalStatus },
    ) => {
        const goalWithNewStatus = {
            ...goal,
            status: e.value as GoalStatus,
        };
        onGoalStatusChange(goalWithNewStatus);
    };
    const showCompleteButton =
        goal.status === GoalStatus.IN_PROGRESS &&
        tasks.length > 0 &&
        tasks.filter((task) => task.status === GoalTaskStatus.IN_PROGRESS)
            .length === 0;

    return (
        <XStack
            alignItems="center"
            paddingLeft={4}
            paddingVertical={4}
            gap={12}
            width="100%"
        >
            {goal.status !== GoalStatus.DELETED && (
                <XStack flexGrow={1} alignItems="center">
                    <Checkbox checked={false} disabled={true} />
                    <XStack
                        flexGrow={1}
                        alignItems="center"
                        gap={8}
                        paddingLeft={4}
                    >
                        {isAddingNewTask ? (
                            <>
                                <AddNewTextAreaEditor
                                    value={newTaskDescription}
                                    onChange={setNewTaskDescription}
                                    onSubmit={onPressSubmit}
                                />
                                <PRButton
                                    label="Add"
                                    size="small"
                                    severity="success"
                                    outlined
                                    onClick={onPressSubmit}
                                />
                                <PRButton
                                    size="small"
                                    severity="secondary"
                                    outlined
                                    onClick={reset}
                                >
                                    <Icon icon={XIcon} />
                                </PRButton>
                            </>
                        ) : (
                            <BodyMediumRegular
                                width="100%"
                                color="$gray800"
                                padding={8}
                                borderRadius={6}
                                borderWidth={1}
                                borderColor="transparent"
                                hoverStyle={{
                                    cursor: 'text',
                                    borderColor: '$gray300',
                                }}
                                onPress={() => {
                                    setIsAddingNewTask(true);
                                }}
                            >
                                New task
                            </BodyMediumRegular>
                        )}
                    </XStack>
                </XStack>
            )}
            <XStack
                flexShrink={1}
                justifyContent="flex-end"
                alignItems="center"
                borderLeftWidth={1}
                borderColor="$gray800"
                paddingLeft={24}
            >
                {showCompleteButton ? (
                    <Button
                        variant="primary"
                        size="small"
                        tone="positive"
                        onPress={() => {
                            onChangeGoalStatus({
                                value: GoalStatus.COMPLETE,
                            });
                        }}
                    >
                        Mark Goal as Completed
                    </Button>
                ) : (
                    <>
                        <BodySmallEmphasized>
                            Change Goal status:
                        </BodySmallEmphasized>
                        <Dropdown
                            onChange={onChangeGoalStatus}
                            options={GoalStatusOptions.filter(
                                ({ value }) => value !== goal.status,
                            )}
                            placeholder="New status"
                            style={{ transform: 'scale(0.85)' }}
                            panelStyle={{ fontSize: '0.9rem' }}
                            variant="filled"
                        />
                    </>
                )}
            </XStack>
        </XStack>
    );
};

const GoalStatusOptions = [
    { label: 'Active', value: GoalStatus.IN_PROGRESS },
    { label: 'Draft', value: GoalStatus.DRAFT },
    { label: 'Completed', value: GoalStatus.COMPLETE },
    { label: 'Archived', value: GoalStatus.DELETED },
];

const TextAreaEditor = (
    options:
        | ColumnEditorOptions
        | { value: string; editorCallback: (value: string) => void },
) => {
    return (
        <InputTextarea
            autoResize
            style={{ width: '100%' }}
            value={options.value as string}
            onChange={(e) => {
                options.editorCallback?.(e.target.value);
            }}
            onKeyDown={(e) => {
                e.stopPropagation();
            }}
            tooltip="Supports [text](url) for links, **text** for bold"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
        />
    );
};

const AddNewTextAreaEditor = ({
    value,
    onChange,
    onSubmit,
}: {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
}) => {
    return (
        <InputTextarea
            autoResize
            style={{ width: '100%', height: 40 }}
            value={value.trimStart()}
            onChange={(e) => {
                onChange(e.target.value);
            }}
            onKeyDown={(e) => {
                e.stopPropagation();
                e.key === 'Enter' && !e.shiftKey && onSubmit();
            }}
            tooltip="Supports [text](url) for links, **text** for bold"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
        />
    );
};
